import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Fingerprint } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { loginWithPasskey, passkeysSupported } from "@/lib/passkeys";
import { toast } from "sonner";

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [bioLoading, setBioLoading] = useState(false);
  const [bioAvailable, setBioAvailable] = useState(false);
  const { login, refresh } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    setBioAvailable(passkeysSupported());
    const saved = window.localStorage.getItem("zuntria_passkey_email");
    if (saved) setEmail(saved);
  }, []);

  const biometricLogin = async () => {
    const target = email.trim() || window.localStorage.getItem("zuntria_passkey_email");
    if (!target) {
      toast.error("Enter your email first, then tap Face ID / Fingerprint");
      return;
    }
    setBioLoading(true);
    try {
      await loginWithPasskey(target);
      window.localStorage.setItem("zuntria_passkey_email", target);
      await refresh();
      toast.success("Welcome back!");
      nav("/dashboard");
    } catch (err) {
      if (err?.name === "NotAllowedError") toast.error("Biometric prompt cancelled");
      else toast.error(err?.response?.data?.detail || err?.message || "Biometric login failed");
    } finally {
      setBioLoading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back!");
      nav("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="mx-auto flex min-h-[80vh] max-w-md flex-col justify-center px-6 py-12">
      <div className="rounded-2xl border border-border bg-card p-8">
        <h1 className="font-display text-2xl font-medium tracking-tight">Welcome back</h1>
        <p className="mt-1 text-sm text-muted-foreground">Sign in to your Zuntria account.</p>

        <form onSubmit={submit} className="mt-8 space-y-5" data-testid="login-form">
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="h-11" data-testid="login-email-input" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <Input id="password" type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} className="h-11" data-testid="login-password-input" />
          </div>
          <Button type="submit" className="h-11 w-full" disabled={loading} data-testid="login-submit-button">
            {loading ? "Signing in…" : "Sign in"}
          </Button>
        </form>

        <div className="relative my-6 text-center">
          <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-border" /></div>
          <span className="relative bg-card px-3 text-xs uppercase tracking-[0.2em] text-muted-foreground">or</span>
        </div>

        <Button variant="outline" className="h-11 w-full" onClick={googleLogin} data-testid="google-login-btn">
          Continue with Google
        </Button>

        {bioAvailable && (
          <Button variant="outline" className="mt-3 h-11 w-full" onClick={biometricLogin} disabled={bioLoading} data-testid="passkey-login-btn">
            <Fingerprint className="mr-2 h-4 w-4" />
            {bioLoading ? "Waiting for biometrics…" : "Sign in with Face ID / Fingerprint"}
          </Button>
        )}

        <p className="mt-6 text-center text-sm text-muted-foreground">
          New here? <Link to="/signup" className="text-primary hover:underline">Create an account</Link>
        </p>
      </div>
    </div>
  );
}
